import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Alert,
  Button,
  Text,
  MandatoryFieldText,
  SendToast,
} from "@networkraildigitalfactory/components";
import { Regexes, toTop } from "../../services/utils";
import styles from "./MyProfile.module.css";
import ProfileInputs from "./ProfileInput";
import { useUsers } from "../../hooks/useAPI";
import { LinkAccountsModal } from "../Modals";
import useLinkAccountsModal from "../../hooks/modals/useLinkAccountsModal";
import useInitialiseCentralFunctions from "../../hooks/centralFunctions/useInitialiseCentralFunctions";

const MyProfile = (props) => {
  const { watch, register, handleSubmit, errors, reset, formState, control } = useForm();
  const { isModalOpen, toggleModal } = useLinkAccountsModal();
  const { dirty: isDirty } = formState;
  const { setIsDirtyForm, user, updateUser } = props;
  const { email } = user;
  const [profileFormValues, setProfileFormValues] = useState(user);

  useInitialiseCentralFunctions(email);

  useEffect(() => {
    setIsDirtyForm(isDirty);
  }, [isDirty]);

  const [{ error: errPut }, executePut] = useUsers(
    {
      url: email,
      method: "PUT",
    },
    { manual: true }
  );

  const formatEmpNo = (empNo) => {
    let formattedEmpNo = empNo;
    switch (empNo.length) {
      case 4:
        formattedEmpNo = `0${empNo}`;
        break;
      case 3:
        formattedEmpNo = `00${empNo}`;
        break;
      default:
    }
    return formattedEmpNo;
  };

  const onSubmit = async () => {
    const empNo = formatEmpNo(profileFormValues.empNo);
    try {
      const result = await executePut({
        data: {
          fullName: profileFormValues.fullName,
          email: profileFormValues.email,
          jobTitle: profileFormValues.jobTitle,
          empNo,
          centralFunction: profileFormValues.centralFunction,
          lineManager: profileFormValues.lineManager,
          additionalLM: profileFormValues.additionalLM,
        },
      });
      reset({
        dirty: false,
      });
      updateUser(result.data);
      setProfileFormValues(result.data);
      toTop();
      SendToast("Your profile has been saved", "success");
    } catch (error) {
      console.error(error);
    }
  };

  if (errPut) return SendToast("Your profile hasn't been saved. Please try again", "error");

  const emailValidation = (value, field) => {
    if (value === watch("email")) {
      return "Must be different from your own email";
    }

    if (
      (field === "LM" && value === watch("additionalLM")) ||
      (field === "AdditionalLM" && value === watch("lineManager"))
    ) {
      return "Line Manager and Additional Line Manager cannot be the same person.";
    }

    return true;
  };

  const profileInputs = [
    {
      labelText: "Full Name",
      exampleText: "For example Joe Bloggs",
      forLabel: "tabToMe",
      required: true,
      name: "fullName",
      id: "tabToMe",
    },
    {
      labelText: "Email address",
      forLabel: "emailAddress",
      exampleText: "",
      required: true,
      name: "email",
      readOnly: true,
      pattern: Regexes.email,
    },
    {
      labelText: "Job title",
      forLabel: "jobTitle",
      exampleText: "For example senior project manager",
      required: true,
      name: "jobTitle",
    },
    {
      labelText: "Function",
      forLabel: "centralFunction",
      exampleText: "",
      required: true,
      name: "centralFunction",
      isThirdPartyComponent: true,
    },
    {
      labelText: "Employee number",
      forLabel: "employeeNumber",
      exampleText: (
        <>
          Your employee number can be found on Ebusiness by navigating to `Employee self service -
          My Information` <br /> or on any of your payslips.
        </>
      ),
      required: true,
      name: "empNo",
      minLength: 3,
      maxLength: 6,
      pattern: Regexes.numeric,
      patternErrorMessage: "Must be numeric with no spaces",
    },
    {
      labelText: "Line manager’s email",
      forLabel: "lineManagerEmail",
      exampleText: email.includes("gbrtt.co.uk")
        ? "For example line.manager@gbrtt.co.uk"
        : "For example line.manager@networkrail.co.uk",
      required: true,
      name: "lineManager",
      pattern: Regexes.email,
      patternErrorMessage: "Must be a valid email",
      validate: (value) => emailValidation(value, "LM"),
    },
    {
      labelText: "Additional email address",
      forLabel: "additionalEmailAddress",
      exampleText: (
        <>
          If you wish someone other than your line manager to view your objectives and 1-2-1 notes,
          please enter their email address here <br />
          (for example graduate or secondment manager).
        </>
      ),
      required: false,
      name: "additionalLM",
      pattern: Regexes.email,
      patternErrorMessage: "Must be a valid email",
      validate: (value) => emailValidation(value, "AdditionalLM"),
    },
  ];

  return (
    <div className={styles.Profile}>
      {isDirty && (
        <Alert
          color="red"
          title="Unsaved Changes"
          message="You must save your changes before navigating to another page. If you do not select save,
        all changes will be lost."
          style={{ lineHeight: "140%", letterSpacing: "0.0015em", padding: "8px 10px 8px 10px" }}
        />
      )}
      <Text type="body" wrapper="p" size="16px">
        Please fill out the profile information below. You will need to ensure you keep the line
        manager details up-to-date, as the manager listed in this profile page will receive all
        1-2-1s, objectives and feedback.
      </Text>
      <Text type="body" wrapper="p" size="16px">
        Recently undergone a change of name or personal details? Click{" "}
        <span
          role="button"
          tabIndex={0}
          name="open link accounts modal"
          aria-label="open link accounts modal"
          className={styles.text_button}
          id="openLinkAccountButton"
          onClick={toggleModal}
          onKeyPress={() => {}}
        >
          here
        </span>{" "}
        to link your iPerform accounts together.
      </Text>
      <MandatoryFieldText />
      <form onSubmit={handleSubmit(onSubmit)}>
        <ProfileInputs
          inputs={profileInputs}
          errors={errors}
          profileFormValues={profileFormValues}
          setProfileFormValues={setProfileFormValues}
          register={register}
          Controller={Controller}
          control={control}
        />
        <div className={styles.button_wrapper}>
          <Button text="Save" color="blue" />
        </div>
      </form>
      <LinkAccountsModal show={isModalOpen} close={toggleModal} />
    </div>
  );
};

export default MyProfile;
